import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, SlidersHorizontal, Timer,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Field, InfoTip, UnitInput, inputCls, grid2, grid3 } from '../components/shared/FormKit';
import {
  SettingsCard, SettingsRow, Toggle, CheckList, StaffPicker, chipOn, chipOff, chipWarn,
  pad, TimeInput, RemoveTimeButton, AddTimeButton, DailyTimesEditor,
} from '../components/shared/SettingsKit';
import MailPreview from '../components/shared/MailPreview';
import { fetchJSON } from '../utils/api';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The three overdue categories — the keys each scheduled send time carries in
// reminderSchedules[].categories and the countdown's *CountdownDays fields
// (same names as the API).
const CATEGORIES = [
  { key: 'lateAccounts', label: 'Late Accounts', countdownField: 'lateAccountsCountdownDays', countdownSwitch: 'lateAccountsCountdownEnabled' },
  { key: 'lateArchival', label: 'Late Archival', countdownField: 'lateArchivalCountdownDays', countdownSwitch: 'lateArchivalCountdownEnabled' },
  { key: 'fsSigning', label: 'Late FS Signing', countdownField: 'fsSigningCountdownDays', countdownSwitch: 'fsSigningCountdownEnabled' },
];
const CATEGORY_KEYS = CATEGORIES.map(c => c.key);
const categoryLabel = (key) => (CATEGORIES.find(c => c.key === key) || {}).label || key;
const sortCategories = (keys) => CATEGORY_KEYS.filter(k => (keys || []).includes(k));
const timeLabel = (s) => `${DAYS_SHORT[s.dayOfWeek]} ${pad(s.hour)}:${pad(s.minute)}`;

// Every numeric field: whole number within the range the API enforces — days
// 0–3650, the Late Accounts deadline months 0–12. The inputs keep whatever was
// typed so an emptied box stays empty (never turns into a silent 0); Save
// refuses until every box on that tab is valid.
const DAYS_MAX = 3650;
const MONTHS_MAX = 12;
const CATEGORY_DAY_FIELDS = [
  'lateAccountsRemindBeforeDays',
  'lateArchivalRemindBeforeDays',
  'fsSigningRemindBeforeDays',
];
const COUNTDOWN_DAY_FIELDS = CATEGORIES.map(c => c.countdownField);
const DAY_FIELDS = [...CATEGORY_DAY_FIELDS, ...COUNTDOWN_DAY_FIELDS];
// The Late Accounts deadline rule — months after the FYE, listed and non-listed.
const MONTH_FIELDS = ['lateAccountsMonthsAfterFyeListed', 'lateAccountsMonthsAfterFye'];
const NUMBER_FIELDS = [...DAY_FIELDS, ...MONTH_FIELDS];
const wholeNumberError = (v, max, unit) => {
  const s = String(v ?? '').trim();
  if (s === '') return `Enter a number of ${unit}`;
  if (!/^\d+$/.test(s)) return 'Whole numbers only (no decimals or minus sign)';
  if (Number(s) > max) return `At most ${max} ${unit}`;
  return null;
};
const dayError = (v) => wholeNumberError(v, DAYS_MAX, 'days');
const monthError = (v) => wholeNumberError(v, MONTHS_MAX, 'months');
// The error for whichever unit that field carries; non-numeric fields: none.
const numberError = (k, v) => {
  if (MONTH_FIELDS.includes(k)) return monthError(v);
  if (DAY_FIELDS.includes(k)) return dayError(v);
  return null;
};

// Hover help next to a field label: what the number means + a worked example.
const fieldTip = (body, example, align = 'left') => (
  <InfoTip align={align} className="ml-0.5" text={
    <>
      {body && <span className="block mb-1.5">{body}</span>}
      <span className="block"><span className="font-semibold">Example:</span> {example}</span>
    </>
  } />
);

// The page groups by concern so future overdue categories slot in without the
// page turning into one long scroll:
//   Overdue categories — one card per category (deadline rule + mail scope);
//                        a new category = one more <SettingsCard>.
//   Scheduled reminder — the overdue mail: master switch, who, the send times
//                        (each ticks the categories it mails) + a preview of
//                        the mail a chosen send time produces, and Send now.
//   Deadline countdown — the daily "due within N days" mail: master switch,
//                        who, per-category horizon, daily send times + its own
//                        preview and Send now.
const TABS = [
  { id: 'categories', name: 'Overdue Categories', icon: SlidersHorizontal },
  { id: 'scheduled', name: 'Scheduled Reminder', icon: Mail },
  { id: 'countdown', name: 'Deadline Countdown', icon: Timer },
];

// Each tab saves only its own fields (the API takes a partial patch), so
// unsaved edits on another tab are left untouched.
const TAB_FIELDS = {
  categories: [
    ...MONTH_FIELDS, ...CATEGORY_DAY_FIELDS,
    'lateAccountsStatuses', 'lateAccountsServices', 'lateArchivalStatuses', 'fsSigningStatuses',
  ],
  scheduled: [
    'reminderEnabled', 'reminderNotifyPartner', 'reminderNotifyDirector', 'reminderNotifyManager',
    'summaryEnabled', 'summaryRecipientStaffIds', 'reminderSchedules',
  ],
  countdown: [
    'countdownEnabled', 'countdownNotifyPartner', 'countdownNotifyDirector', 'countdownNotifyManager',
    'countdownSummaryEnabled', 'countdownSummaryRecipientStaffIds',
    ...CATEGORIES.map(c => c.countdownSwitch), ...COUNTDOWN_DAY_FIELDS, 'countdownSchedules',
  ],
};

// Comparable form of a value, so re-ticking a box or retyping the same number
// does not count as an unsaved change.
const normValue = (k, v) => {
  if (NUMBER_FIELDS.includes(k)) return String(v ?? '').trim();
  if (k === 'reminderSchedules') {
    return JSON.stringify((v || []).map(s => [s.dayOfWeek, s.hour, s.minute, sortCategories(s.categories)]));
  }
  if (k === 'countdownSchedules') {
    return JSON.stringify((v || []).map(s => [s.hour, s.minute]));
  }
  if (Array.isArray(v)) return JSON.stringify(v.map(String).sort());
  return JSON.stringify(v ?? null);
};
const pick = (obj, keys) => Object.fromEntries(keys.map(k => [k, obj[k]]));

const SettingsOverdue = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('settings:overdue:edit');

  const [activeTab, setActiveTab] = useState('categories');
  const [form, setForm] = useState(null);
  const [saved, setSaved] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Per tab: { saving, error, savedMsg }.
  const [saveState, setSaveState] = useState({});
  const [savedTick, setSavedTick] = useState(0);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggleInArray = (k, value) => setForm(prev => {
    const arr = prev[k] || [];
    return { ...prev, [k]: arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value] };
  });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, st, sv, so] = await Promise.all([
        fetchJSON('/api/qm/overdue-settings'),
        fetchJSON('/api/settings/lookups/engagement_status?active=true'),
        fetchJSON('/api/settings/lookups/service_provided?active=true'),
        fetchJSON('/api/qm/staff/options?active=true'),
      ]);
      setForm(s.settings);
      setSaved(s.settings);
      setStatuses((st.options || []).map(o => o.value));
      setServices((sv.options || []).map(o => o.value));
      setStaff(so.staff || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canEdit) load(); }, [canEdit, load]);

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need master data editing rights to access this area.</p>
        </div>
      </div>
    );
  }

  const patchSaveState = (tab, patch) => setSaveState(prev => ({ ...prev, [tab]: { ...prev[tab], ...patch } }));

  const isDirty = (tab) => !!form && !!saved
    && TAB_FIELDS[tab].some(k => normValue(k, form[k]) !== normValue(k, saved[k]));

  const save = async (tab) => {
    const fields = TAB_FIELDS[tab];
    // A switched-off countdown category keeps whatever its days box holds;
    // only the switched-on ones must be valid.
    const offCountdown = CATEGORIES.filter(c => form[c.countdownSwitch] !== true).map(c => c.countdownField);
    if (fields.some((k) => NUMBER_FIELDS.includes(k) && !offCountdown.includes(k) && numberError(k, form[k]))) {
      patchSaveState(tab, { error: 'Fix the highlighted number fields before saving.', savedMsg: false });
      return;
    }
    if (tab === 'scheduled' && !(form.reminderSchedules || []).length) {
      patchSaveState(tab, { error: 'Add at least one send time before saving.', savedMsg: false });
      return;
    }
    if (tab === 'scheduled' && (form.reminderSchedules || []).some(s => !(s.categories || []).length)) {
      patchSaveState(tab, { error: 'Tick at least one category on every send time before saving.', savedMsg: false });
      return;
    }
    if (tab === 'countdown' && !(form.countdownSchedules || []).length) {
      patchSaveState(tab, { error: 'Add at least one send time before saving.', savedMsg: false });
      return;
    }
    patchSaveState(tab, { saving: true, error: null, savedMsg: false });
    try {
      const payload = pick(form, fields);
      fields.filter(k => NUMBER_FIELDS.includes(k)).forEach((k) => {
        // An invalid box on a switched-off countdown category is not sent.
        if (offCountdown.includes(k) && numberError(k, form[k])) delete payload[k];
        else payload[k] = Number(form[k]);
      });
      const data = await fetchJSON('/api/qm/overdue-settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setSaved(data.settings);
      setForm(prev => ({ ...prev, ...pick(data.settings, fields) }));
      patchSaveState(tab, { saving: false, savedMsg: true });
      setSavedTick(t => t + 1);
      setTimeout(() => patchSaveState(tab, { savedMsg: false }), 2500);
    } catch (e) {
      patchSaveState(tab, { saving: false, error: e.message });
    }
  };

  const saveControls = (tab) => {
    const st = saveState[tab] || {};
    const dirty = isDirty(tab);
    return (
      <div className="flex items-center gap-3 shrink-0">
        {st.savedMsg && <span className="text-sm text-green-600">Saved</span>}
        {dirty && !st.saving && !st.savedMsg && <span className="text-xs text-amber-700">Unsaved changes</span>}
        <button onClick={() => save(tab)} disabled={st.saving || !dirty} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
          {st.saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    );
  };

  const saveError = (tab) => {
    const msg = saveState[tab]?.error;
    return msg ? (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center gap-2">
        <span>{msg}</span>
      </div>
    ) : null;
  };

  // A failed initial load leaves form null — show the error with a retry
  // instead of sitting on "Loading…" forever (backlog #3).
  if (!form && !loading && error) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Failed to load settings</h2>
          <p className="text-sm text-red-600 mt-1">{error}</p>
          <button
            onClick={load}
            className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading || !form) {
    return <div className="p-8 text-center text-slate-500">Loading…</div>;
  }

  // Send times of the scheduled reminder: one row per {dayOfWeek, hour,
  // minute, categories}. The list never goes empty here (the last row cannot
  // be removed); the API merges duplicate times on save.
  const schedules = form.reminderSchedules || [];
  const setSchedule = (i, patch) => setForm(prev => ({
    ...prev,
    reminderSchedules: (prev.reminderSchedules || []).map((s, j) => (j === i ? { ...s, ...patch } : s)),
  }));
  const addSchedule = () => setForm(prev => {
    const list = prev.reminderSchedules || [];
    const last = list[list.length - 1];
    // A new row = the next day at the same time (same categories), so two
    // clicks never collide.
    const next = last
      ? { ...last, dayOfWeek: (last.dayOfWeek + 1) % 7, categories: [...(last.categories || [])] }
      : { dayOfWeek: 1, hour: 9, minute: 0, categories: [...CATEGORY_KEYS] };
    return { ...prev, reminderSchedules: [...list, next] };
  });
  const removeSchedule = (i) => setForm(prev => ({
    ...prev,
    reminderSchedules: (prev.reminderSchedules || []).filter((_, j) => j !== i),
  }));
  const toggleScheduleCategory = (i, key) => setForm(prev => ({
    ...prev,
    reminderSchedules: (prev.reminderSchedules || []).map((s, j) => {
      if (j !== i) return s;
      const cur = s.categories || [];
      return { ...s, categories: sortCategories(cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]) };
    }),
  }));

  // What the category cards print about each mail — read from the form (what
  // will apply once that tab is saved).
  const sendTimesFor = (key) => schedules.filter(s => (s.categories || []).includes(key)).map(timeLabel);
  const countdownFor = (c) => ({
    enabled: form.countdownEnabled && form[c.countdownSwitch] === true,
    days: Number(form[c.countdownField]) || 0,
  });
  const roleToggles = (prefix) => (
    <div className="flex flex-wrap gap-4">
      <Toggle checked={form[`${prefix}Partner`]} onChange={v => setField(`${prefix}Partner`, v)} label="Partner" />
      <Toggle checked={form[`${prefix}Director`]} onChange={v => setField(`${prefix}Director`, v)} label="Director" />
      <Toggle checked={form[`${prefix}Manager`]} onChange={v => setField(`${prefix}Manager`, v)} label="Manager" />
    </div>
  );
  // The Recipients row of either mail: the roles whose leads get their own
  // mail, then the summary switch with its recipients. `prefix` names the
  // role fields (reminderNotify / countdownNotify), `summary` the switch and
  // its id list.
  const recipientControls = (prefix, summary) => (
    <>
      {roleToggles(prefix)}
      <div>
        <Toggle checked={form[summary.enabled]} onChange={v => setField(summary.enabled, v)} label="Also send a full summary email" />
        {form[summary.enabled] && (
          <div className="mt-3">
            <p className="text-xs text-slate-500 mb-1.5">Summary Recipients (Search Staff):</p>
            <StaffPicker staff={staff} selectedIds={form[summary.ids]} onChange={ids => setField(summary.ids, ids)} />
          </div>
        )}
      </div>
    </>
  );
  // Header chips of a category card: how each mail treats this category, read
  // from the other tabs — scheduled: one chip per send time, amber when in
  // none, Off while that mail is switched off; countdown: its horizon, or Off.
  const mailChips = (c) => {
    const times = sendTimesFor(c.key);
    const cd = countdownFor(c);
    return (
      <>
        <button
          type="button"
          onClick={() => setActiveTab('scheduled')}
          title="Change on the Scheduled Reminder tab"
          className="inline-flex flex-wrap items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700"
        >
          <span>Scheduled</span>
          {!form.reminderEnabled
            ? <span className={chipOff}>Off</span>
            : times.length
              ? times.map((t) => <span key={t} className={chipOn}>{t}</span>)
              : <span className={chipWarn}>Not in any send time</span>}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('countdown')}
          title="Change on the Deadline Countdown tab"
          className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700"
        >
          <span>Countdown</span>
          {!cd.enabled
            ? <span className={chipOff}>Off</span>
            : <span className={chipOn}>{cd.days > 0 ? `${cd.days} day${cd.days === 1 ? '' : 's'} before` : 'Deadline day'}</span>}
        </button>
      </>
    );
  };

  // "Statuses covered" of a category's Reminder emails row.
  const statusList = (field, hint = null) => (
    <Field label="Statuses Covered" hint={hint}>
      <CheckList options={statuses} selected={form[field]} onToggle={v => toggleInArray(field, v)} />
    </Field>
  );

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Overdue Rules & Reminder</h1>
        <p className="text-sm text-slate-500 mt-1">Set what counts as overdue and who gets the reminder emails. Each tab has its own Save; saved changes apply to the dashboards and the reminders immediately.</p>
      </div>

      <div className="border-b border-slate-200 mb-5 flex flex-wrap gap-1">
        {TABS.map(({ id, name, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {name}
            {TAB_FIELDS[id] && isDirty(id) && (
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Unsaved changes" />
            )}
          </button>
        ))}
      </div>

      {activeTab === 'categories' && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <p className="min-w-0 flex-1 text-sm text-slate-600">
              One card per overdue category: the <span className="font-medium text-slate-800">deadline</span> the QM dashboard and both reminder emails share, and what the <span className="font-medium text-slate-800">reminder emails</span> cover. Who receives the emails and when is set on the other two tabs.
            </p>
            {saveControls('categories')}
          </div>
          {saveError('categories')}

          <SettingsCard
            title="Late Accounts"
            hint="Financial statements not yet signed by the statutory deadline after the financial year end."
            aside={mailChips(CATEGORIES[0])}
          >
            {/* 上市與非上市各一個期限——上市公司的股東會期限較短。是否上市由
                QC6/QC7 的 PIE 答案寫進 engagement（is_listed）。 */}
            <SettingsRow
              title="Deadline"
              description="Shared by the dashboard and both reminder emails; an engagement is Late from the day after. Listed = PIE with type “Publicly traded entity” on the approved QC6 / QC7 — every other company, including one whose QC6 / QC7 is not approved yet, is non-listed."
            >
              <div className={grid2}>
                <Field
                  label={<>Listed companies: FYE + {fieldTip(null, '4 months — FYE 31 Dec 2025 → deadline 30 Apr 2026; Late from 1 May 2026.')}</>}
                  error={monthError(form.lateAccountsMonthsAfterFyeListed)}
                >
                  <UnitInput unit="months" max={MONTHS_MAX} value={form.lateAccountsMonthsAfterFyeListed} onChange={v => setField('lateAccountsMonthsAfterFyeListed', v)} />
                </Field>
                <Field
                  label={<>Non-listed companies: FYE + {fieldTip(
                    'A month-end FYE keeps to the month end: 31 Aug 2025 + 6 months → 28 Feb 2026.',
                    '6 months — FYE 31 Dec 2025 → deadline 30 Jun 2026; Late from 1 Jul 2026.',
                  )}</>}
                  error={monthError(form.lateAccountsMonthsAfterFye)}
                >
                  <UnitInput unit="months" max={MONTHS_MAX} value={form.lateAccountsMonthsAfterFye} onChange={v => setField('lateAccountsMonthsAfterFye', v)} />
                </Field>
              </div>
              <Field label="Services Covered" hint="Only these services count as Late Accounts — on the dashboard tab and in the emails.">
                <CheckList options={services} selected={form.lateAccountsServices} onToggle={v => toggleInArray('lateAccountsServices', v)} />
              </Field>
            </SettingsRow>
            <SettingsRow title="Reminder Emails" description={MAIL_SCOPE_DESC}>
              <Field
                label={<>Scheduled reminder also lists cases due within {fieldTip(
                  'Cases past the deadline are always listed; this adds the ones due soon.',
                  '30 days — deadline 30 Apr 2026 → in the scheduled reminder from 31 Mar 2026.',
                )}</>}
                error={dayError(form.lateAccountsRemindBeforeDays)}
              >
                <UnitInput unit="days" max={DAYS_MAX} value={form.lateAccountsRemindBeforeDays} onChange={v => setField('lateAccountsRemindBeforeDays', v)} />
              </Field>
              {statusList('lateAccountsStatuses')}
            </SettingsRow>
          </SettingsCard>

          <SettingsCard
            title="Late Archival"
            hint="Audit file not archived by the archival deadline."
            aside={mailChips(CATEGORIES[1])}
          >
            <SettingsRow title="Deadline" description="Shared by the dashboard and both reminder emails; an engagement is Late from the day after.">
              <p className="text-sm text-slate-700">Sign-off date plus the number of days set for the period it falls in, or the manual archival deadline on the engagement.</p>
            </SettingsRow>
            <SettingsRow title="Reminder Emails" description={MAIL_SCOPE_DESC}>
              <Field
                label={<>Scheduled reminder also lists cases due within {fieldTip(
                  'Cases past the deadline are always listed; this adds the ones due soon.',
                  '30 days — deadline 20 Apr 2026 → in the scheduled reminder from 21 Mar 2026.',
                )}</>}
                error={dayError(form.lateArchivalRemindBeforeDays)}
              >
                <UnitInput unit="days" max={DAYS_MAX} value={form.lateArchivalRemindBeforeDays} onChange={v => setField('lateArchivalRemindBeforeDays', v)} />
              </Field>
              {statusList('lateArchivalStatuses')}
            </SettingsRow>
          </SettingsCard>

          <SettingsCard
            title="Late FS Signing"
            hint="Financial statements not signed by the date the QC6 / QC7 expected."
            aside={mailChips(CATEGORIES[2])}
          >
            <SettingsRow title="Deadline" description="Shared by the dashboard and both reminder emails; an engagement is Late from the day after, until the signed FS is recorded.">
              <p className="text-sm text-slate-700">Expected Financial Statements Signing Date on the approved QC6 / QC7 Conclusion.</p>
            </SettingsRow>
            <SettingsRow title="Reminder Emails" description={MAIL_SCOPE_DESC}>
              <Field
                label={<>Scheduled reminder also lists cases due within {fieldTip(
                  'Cases past the expected date are always listed; this adds the ones due soon.',
                  '7 days — expected 15 Jun 2026 → in the scheduled reminder from 8 Jun 2026.',
                )}</>}
                error={dayError(form.fsSigningRemindBeforeDays)}
              >
                <UnitInput unit="days" max={DAYS_MAX} value={form.fsSigningRemindBeforeDays} onChange={v => setField('fsSigningRemindBeforeDays', v)} />
              </Field>
              {statusList('fsSigningStatuses', 'Leave closed statuses (Completed, Discontinued…) unticked — they never get signed financial statements.')}
            </SettingsRow>
          </SettingsCard>
        </div>
      )}

      {activeTab === 'scheduled' && (
        <div className="space-y-4">
          {saveError('scheduled')}
          <SettingsCard
            title="Scheduled Reminder"
            hint="The overdue email: everything past its deadline plus cases due soon, per category. Each send time mails the categories ticked on its row — one email per recipient covering just those."
            aside={saveControls('scheduled')}
          >
            <SettingsRow title="Enabled" description="Switch the scheduled reminder on or off. Send now on the preview below works either way.">
              <Toggle checked={form.reminderEnabled} onChange={v => setField('reminderEnabled', v)} label="Enable scheduled reminder" />
            </SettingsRow>
            <SettingsRow
              title="Recipients"
              description="Every lead of the ticked roles gets one email of only their own items (merged, not one per case) — all partners on a case, not just the first. The address is the staff record linked to the lead. The summary email is the whole overview, to the staff you pick."
            >
              {recipientControls('reminderNotify', { enabled: 'summaryEnabled', ids: 'summaryRecipientStaffIds' })}
            </SettingsRow>
            <SettingsRow
              title="Send Times"
              description="Singapore time. The reminder goes out at every time listed, covering the categories ticked on that row. A row with the same day and time as an earlier one is merged on save."
            >
              <div className="space-y-2">
                {schedules.map((s, i) => {
                  const cats = s.categories || [];
                  return (
                    <div key={i} className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border p-2 ${cats.length ? 'border-slate-200' : 'border-red-300 bg-red-50'}`}>
                      <div className="w-36">
                        <select
                          value={s.dayOfWeek}
                          onChange={e => setSchedule(i, { dayOfWeek: Number(e.target.value) })}
                          className={inputCls}
                          aria-label="Send on"
                        >
                          {DAYS.map((d, di) => <option key={di} value={di}>{d}</option>)}
                        </select>
                      </div>
                      <TimeInput value={s} onChange={(patch) => setSchedule(i, patch)} label="At" />
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                        {CATEGORIES.map(c => (
                          <label key={c.key} className="inline-flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none">
                            <input type="checkbox" checked={cats.includes(c.key)} onChange={() => toggleScheduleCategory(i, c.key)} />
                            {c.label}
                          </label>
                        ))}
                      </div>
                      {!cats.length && <span className="text-xs text-red-600">Tick at least one category</span>}
                      <RemoveTimeButton onClick={() => removeSchedule(i)} disabled={schedules.length <= 1} />
                    </div>
                  );
                })}
              </div>
              <AddTimeButton onClick={addSchedule} />
            </SettingsRow>
          </SettingsCard>

          <ScheduledPreview refreshKey={savedTick} schedules={saved.reminderSchedules || []} />
        </div>
      )}

      {activeTab === 'countdown' && (
        <div className="space-y-4">
          {saveError('countdown')}
          <SettingsCard
            title="Deadline Countdown"
            hint="A daily nudge before a deadline: from N days before until the deadline day, each lead gets one email a day listing their engagements due within that window. Nothing already past the deadline — that is the scheduled reminder's job."
            aside={saveControls('countdown')}
          >
            <SettingsRow title="Enabled" description="Switch the deadline countdown on or off. Send now on the preview below works either way.">
              <Toggle checked={form.countdownEnabled} onChange={v => setField('countdownEnabled', v)} label="Enable deadline countdown" />
            </SettingsRow>
            <SettingsRow
              title="Recipients"
              description="Every lead of the ticked roles gets one email a day of only their own items. The address is the staff record linked to the lead. The summary email is one overview a day of everything within a countdown window, to the staff you pick."
            >
              {recipientControls('countdownNotify', { enabled: 'countdownSummaryEnabled', ids: 'countdownSummaryRecipientStaffIds' })}
            </SettingsRow>
            <SettingsRow
              title="Categories"
              description="Switch each category on and say how many days before its deadline the countdown starts (0 = the deadline day only). Sent every day, weekends included, up to and including the deadline day."
            >
              <div className={grid3}>
                {CATEGORIES.map((c, i) => {
                  const on = form[c.countdownSwitch] === true;
                  return (
                    <div key={c.key} className={`rounded-lg border border-slate-200 p-3 space-y-3 ${on ? '' : 'bg-slate-50'}`}>
                      <Toggle checked={on} onChange={v => setField(c.countdownSwitch, v)} label={c.label} />
                      <Field
                        label={<>Starts before the deadline {fieldTip(
                          '0 = one email on the deadline day only.',
                          '3 days — deadline 20 Apr 2026 → emailed on 17, 18, 19 and 20 Apr.',
                          i === CATEGORIES.length - 1 ? 'right' : 'left',
                        )}</>}
                        error={on ? dayError(form[c.countdownField]) : null}
                      >
                        <UnitInput unit="days" max={DAYS_MAX} disabled={!on} value={form[c.countdownField]} onChange={v => setField(c.countdownField, v)} />
                      </Field>
                    </div>
                  );
                })}
              </div>
            </SettingsRow>
            <SettingsRow title="Send Times" description="Every day at each time listed (Singapore time). Duplicate times are merged on save.">
              <DailyTimesEditor schedules={form.countdownSchedules || []} onChange={(list) => setField('countdownSchedules', list)} />
            </SettingsRow>
          </SettingsCard>

          <CountdownPreview refreshKey={savedTick} />
        </div>
      )}
    </div>
  );
};

// What a category's Reminder emails row covers — the same sentence on every
// card, so it lives once.
const MAIL_SCOPE_DESC = 'What the emails cover for this category. The statuses apply to both the scheduled reminder and the deadline countdown; the dashboard tab filters status on its own.';

// "2 late account(s), 1 expected FS submission(s)" for one recipient row, limited to
// the categories the previewed mail covers.
const countsOf = (r, categories) => categories.map(c => (
  c === 'lateAccounts' ? `${r.lateAccountsCount} late account(s)`
    : c === 'lateArchival' ? `${r.lateArchivalCount} archival reminder(s)`
      : `${r.fsSigningCount ?? 0} expected FS submission(s)`
)).join(', ');

// The scheduled reminder's preview: pick one of the saved send times and see
// the mail it produces — its categories, its recipients. Send now mails that
// send time's content.
const ScheduledPreview = ({ refreshKey, schedules }) => {
  const [idx, setIdx] = useState(0);
  const slot = schedules[Math.min(idx, schedules.length - 1)] || null;
  const categories = useMemo(() => (slot ? sortCategories(slot.categories) : []), [slot]);
  const catsParam = categories.join(',');
  const describe = useCallback((r) => countsOf(r, categories), [categories]);
  const recipientsUrl = catsParam ? `/api/qm/overdue-reminder/recipients?categories=${encodeURIComponent(catsParam)}` : null;
  const renderUrl = useCallback(
    (key) => `/api/qm/overdue-reminder/render?key=${encodeURIComponent(key)}&categories=${encodeURIComponent(catsParam)}`,
    [catsParam]
  );
  return (
    <MailPreview
      refreshKey={refreshKey}
      recipientsUrl={recipientsUrl}
      renderUrl={renderUrl}
      describe={describe}
      enabledKey="reminderEnabled"
      disabledNote="The scheduled reminder is currently disabled — this is what would be sent if enabled."
      emptyNote="Nobody would receive this email with the saved settings and today's data."
      unmatchedNote="These people get no individual reminder. The items still appear in the summary email."
      send={{
        url: '/api/qm/overdue-reminder/send-now',
        body: { categories },
        covers: `${slot ? timeLabel(slot) : ''} — ${categories.map(categoryLabel).join(', ')}`,
      }}
      head={schedules.length > 0 && (
        <label className="flex flex-wrap items-center gap-2 text-xs text-slate-600 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="font-medium">Send Time</span>
          <select value={Math.min(idx, schedules.length - 1)} onChange={e => setIdx(Number(e.target.value))} className={`${inputCls} py-1.5`} style={{ width: 'auto' }}>
            {schedules.map((s, i) => (
              <option key={i} value={i}>{timeLabel(s)} — {sortCategories(s.categories).map(categoryLabel).join(', ')}</option>
            ))}
          </select>
          <span>The mail this send time produces: its categories, its recipients.</span>
        </label>
      )}
    />
  );
};

// The deadline countdown's preview: today's mail (categories with a countdown,
// their horizons), its recipients. Send now mails today's countdown.
const CountdownPreview = ({ refreshKey }) => {
  const [meta, setMeta] = useState({ categories: [], days: {} });
  useEffect(() => {
    let cancelled = false;
    fetchJSON('/api/qm/overdue-countdown/recipients')
      .then((d) => { if (!cancelled) setMeta({ categories: d.categories || [], days: d.days || {} }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [refreshKey]);
  const renderUrl = useCallback((key) => `/api/qm/overdue-countdown/render?key=${encodeURIComponent(key)}`, []);
  const describe = useCallback((r) => countsOf(r, meta.categories), [meta.categories]);
  const covers = meta.categories.map(c => `${categoryLabel(c)} (${meta.days[c] > 0 ? `${meta.days[c]} days before` : 'deadline day'})`).join(', ');
  return (
    <MailPreview
      refreshKey={refreshKey}
      recipientsUrl="/api/qm/overdue-countdown/recipients"
      renderUrl={renderUrl}
      describe={describe}
      enabledKey="countdownEnabled"
      disabledNote="The deadline countdown is currently disabled — this is what would be sent today if enabled."
      emptyNote={meta.categories.length
        ? 'Nobody has an engagement due within the countdown window today.'
        : 'No category is switched on — switch one on above and save.'}
      unmatchedNote="These people get no individual countdown email. The items still appear in the summary email."
      send={{
        url: '/api/qm/overdue-countdown/send-now',
        body: {},
        covers: covers || 'nothing (no category has a countdown)',
      }}
      head={meta.categories.length > 0 && (
        <p className="text-xs text-slate-600 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="font-medium">Today's countdown covers:</span> {covers}
        </p>
      )}
    />
  );
};

export default SettingsOverdue;
